import React, { useEffect, useState } from 'react';
import { array, bool, func, string } from 'prop-types';
import classNames from 'classnames';

import { parse } from '../../util/urlHelpers';
import {
  ArticleCard,
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  LinkTabNavHorizontal,
  Page,
  PaginationLinks,
} from '../../components';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import {
  FOR_CLIENT_ARTICLE,
  FOR_COACHEE_ARTICLE,
  FOR_COACH_ARTICLE,
} from '../../marketplace-custom-config';
import { propTypes } from '../../util/types';
import { ArticleSearchForm } from '../../forms';
import { searchArticlesByKeyword } from './KnowledgeHubNavigationPage.duck';

import css from './KnowledgeHubNavigationPage.module.css';

const KnowledgeHubNavigationPageComponent = props => {
  const {
    scrollingDisabled,
    intl,
    fetchArticlesInProgress,
    fetchArticlesError,
    pagination,
    articles,
    searchParams,
    onSearchArticlesByKeyword,
  } = props;

  const { category = FOR_CLIENT_ARTICLE } = parse(searchParams);

  const schemaTitle = intl.formatMessage({ id: 'KnowledgeHubNavigationPage.schemaTitle' });
  const tabs = [
    {
      text: (
        <span className={classNames({ [css.activeTab]: category === FOR_CLIENT_ARTICLE })}>
          <FormattedMessage id="KnowledgeHubNavigationPage.forClients" />
        </span>
      ),
      selected: category === FOR_CLIENT_ARTICLE,
      linkProps: {
        name: 'KnowledgeHubNavigationPage',
        to: { search: '?category=for_client' },
      },
    },
    {
      text: (
        <span className={classNames({ [css.activeTab]: category === FOR_COACH_ARTICLE })}>
          <FormattedMessage id="KnowledgeHubNavigationPage.forCoaches" />
        </span>
      ),
      selected: category === FOR_COACH_ARTICLE,
      linkProps: {
        name: 'KnowledgeHubNavigationPage',
        to: { search: '?category=for_coach' },
      },
    },
    {
      text: (
        <span className={classNames({ [css.activeTab]: category === FOR_COACHEE_ARTICLE })}>
          <FormattedMessage id="KnowledgeHubNavigationPage.forCoachees" />
        </span>
      ),
      linkProps: {
        name: 'KnowledgeHubNavigationPage',
        to: { search: '?category=for_coachee' },
      },
      selected: category === FOR_COACHEE_ARTICLE,
    },
  ];

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName="KnowledgeHubNavigationPage"
        pagination={pagination}
        pageSearchParams={parse(searchParams)}
      />
    ) : null;

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer hideSearchbar currentPage="KnowledgeHubNavigationPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <section className={css.hero}>
            <ArticleSearchForm
              className={css.searchForm}
              onSearchArticlesByKeyword={onSearchArticlesByKeyword}
            />
          </section>

          <LinkTabNavHorizontal tabs={tabs} className={css.tabs} />

          {fetchArticlesError && (
            <p className={css.error}>
              <FormattedMessage id="KnowledgeHubNavigationPage.error" />
            </p>
          )}

          {fetchArticlesInProgress ? (
            <p className={css.loading}>
              <FormattedMessage id="KnowledgeHubNavigationPage.loading" />
            </p>
          ) : (
            <>
              <div className={css.articles}>
                {articles.map(article => (
                  <ArticleCard key={article.id} article={article} className={css.article} />
                ))}
              </div>
              {paginationLinks}
            </>
          )}
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

KnowledgeHubNavigationPageComponent.defaultProps = {
  fetchArticlesInProgress: false,
  fetchArticlesError: null,
  pagination: null,
  articles: [],
  scrollingDisabled: false,
  searchParams: null,

  // from injectIntl
  intl: intlShape.isRequired,
};

KnowledgeHubNavigationPageComponent.propTypes = {
  fetchArticlesInProgress: bool,
  fetchArticlesError: propTypes.error,
  pagination: propTypes.pagination,
  articles: array,
  scrollingDisabled: bool.isRequired,
  searchParams: string,
  onSearchArticlesByKeyword: func,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapDispatchToProps = dispatch => ({
  onSearchArticlesByKeyword: keyword => dispatch(searchArticlesByKeyword(keyword)),
});

const mapStateToProps = state => {
  const {
    fetchArticlesInProgress,
    fetchArticlesError,
    pagination,
    articles,
    searchParams,
  } = state.KnowledgeHubNavigationPage;

  return {
    fetchArticlesInProgress,
    fetchArticlesError,
    pagination,
    articles,
    searchParams,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const KnowledgeHubNavigationPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(KnowledgeHubNavigationPageComponent);

export default KnowledgeHubNavigationPage;
